import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowDown, FaArrowUp,
  FaChevronDown, FaChevronUp,
  FaClock, FaCopy, FaExclamationCircle, FaInfoCircle, FaMinus, FaPlay, FaRegSave, FaTerminal, FaTimes
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import ReactJson from 'react-json-view';
import { messageStatus, workflowEvents } from '../../settings/shared/constants';
import { showNotificationGlobal } from '../common/NotificationProvider';
import { useWebSocket } from '../../services/websocket/WebSocketContext';
import SATooltip from '../common/SATooltip';

// Constants
const MAX_RECURSION_DEPTH = 5;
const MAX_LOG_ITEMS = 1000; // Limit to prevent memory issues
const MAX_JSON_DATA = 500;
const MAX_CONSOLE_OUTPUT = 500;
const DEBOUNCE_DELAY = 300;
const APP_SIDEBAR_SELECTOR = '.sax-wrapper > .left';
const CHAT_PANEL_SELECTOR = '.project-main > .main-right.main-right--studio';
const CHAT_RESIZE_HANDLE_SELECTOR = '.project-main > .chat-resize-handle';

// Helper function to sanitize text for XSS prevention
const sanitizeText = (text) => {
  if (typeof text !== 'string') return text;
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Helper function to safely parse JSON with size limit
const safeJsonParse = (str, maxSize = 1000000) => { // 1MB default limit
  if (typeof str !== 'string') return null;
  if (str.length > maxSize) {
    console.warn('JSON string exceeds size limit');
    return null;
  }
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};

const JSON_VIEW_THEME = {
  base00: 'transparent',
  base01: 'transparent',
  base02: 'transparent',
  base03: '#94a3b8', // comments / metadata
  base04: '#64748b',
  base05: '#0f172a', // default text
  base06: '#0f172a',
  base07: '#0f172a',
  base08: '#e11d48', // null / delete / errors
  base09: '#E91E63', // numbers
  base0A: '#3F51B5',
  base0B: '#16a34a', // booleans
  base0C: '#0891b2', // strings
  base0D: '#2563eb', // keys
  base0E: '#7c3aed', // functions
  base0F: '#0ea5e9'
};

const ConsoleDrawer = ({ isOpen, onClose, nodeData, nodeId, sidebarOpen = false, paletteOpen = false, chatVisible = false, chatWidth = 400, focusInput = false, projectId, expandOnOpen = false, onExpandComplete }) => {
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [jsonData, setJsonData] = useState([]);
  const consoleRef = useRef(null);
  const [drawerHeight, setDrawerHeight] = useState(350); // Default height in pixels
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(0);
  const handleResizeRef = useRef(null);
  const handleResizeEndRef = useRef(null);
  const previousHeightRef = useRef(300);
  
  // Column split state
  const [leftColumnWidth, setLeftColumnWidth] = useState(40); // Percentage - default to chat only
  const [isResizingColumn, setIsResizingColumn] = useState(false);
  const isResizingColumnRef = useRef(false);
  const resizeColumnStartXRef = useRef(0);
  const resizeColumnStartWidthRef = useRef(100);
  const handleColumnResizeRef = useRef(null);
  const handleColumnResizeEndRef = useRef(null);
  const chatViewRef = useRef(null);
  const consoleViewRef = useRef(null);
  const chatMessagesScrollRef = useRef(null);

  const [logItems, setLogItems] = useState([]); 
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [messageInput, setMessageInput] = useState('');
  const draftQueryRef = useRef('');
  const sendingQueryRef = useRef(false); 
  const currentAgentIdRef = useRef(null); 
  const currentNodeIdRef = useRef(null); 
  const workflowStopDispatchedRef = useRef(false); // Track if workflow-stop has been dispatched
  const displayedAgentIdsRef = useRef(new Set()); // Track which agent IDs have already displayed their results
  const [isWorkflowRunning, setIsWorkflowRunning] = useState(false);
  const [isWorkflowExecuting, setIsWorkflowExecuting] = useState(false); // Track if workflow is executing (between request_execute and request_completed)
  const [hasAgents, setHasAgents] = useState(false); // Track if workflow has agents (same as Save button condition)
  const [hasTasks, setHasTasks] = useState(false); // Track if workflow has tasks (same as Save button condition)
  const [waitingForResponse, setWaitingForResponse] = useState(null); // Track if waiting for response after sending message
  const messageInputRef = useRef(null); // Ref for the message input field
  
  const { socket, checkConnection, reconnectWithRetry, isConnected, forceReconnect } = useWebSocket();
  const reconnectAttemptRef = useRef(false);
  const connectionCheckedRef = useRef(false);
  const [appSidebarWidth, setAppSidebarWidth] = useState(0);
  const [chatPanelWidth, setChatPanelWidth] = useState(0);
  const [chatHandleWidth, setChatHandleWidth] = useState(0);

  const measureAppSidebarWidth = useCallback(() => {
    // The global left navigation sidebar is outside the workflow canvas, so we must offset fixed positioning.
    const el = document.querySelector(APP_SIDEBAR_SELECTOR);
    const next = el ? el.getBoundingClientRect().width : 0;
    setAppSidebarWidth((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
  }, []);

  const measureChatWidths = useCallback(() => {
    // Chat panel + its resize handle live inside the workspace. When present/visible,
    // the Category panel starts after them.
    const chatEl = document.querySelector(CHAT_PANEL_SELECTOR);
    const handleEl = document.querySelector(CHAT_RESIZE_HANDLE_SELECTOR);

    const isChatActuallyVisible =
      Boolean(chatEl) &&
      !chatEl.classList.contains('hidden') &&
      !chatEl.classList.contains('force-disabled') &&
      chatEl.getBoundingClientRect().width > 0;

    const nextChat = (chatVisible && isChatActuallyVisible) ? chatEl.getBoundingClientRect().width : 0;
    const nextHandle = (chatVisible && isChatActuallyVisible && handleEl) ? handleEl.getBoundingClientRect().width : 0;

    setChatPanelWidth((prev) => (Math.abs(prev - nextChat) > 0.5 ? nextChat : prev));
    setChatHandleWidth((prev) => (Math.abs(prev - nextHandle) > 0.5 ? nextHandle : prev));
  }, [chatVisible]);

  useEffect(() => {
    measureAppSidebarWidth();
    measureChatWidths();
    window.addEventListener('resize', measureAppSidebarWidth);
    window.addEventListener('resize', measureChatWidths);

    const el = document.querySelector(APP_SIDEBAR_SELECTOR);
    const chatEl = document.querySelector(CHAT_PANEL_SELECTOR);
    const handleEl = document.querySelector(CHAT_RESIZE_HANDLE_SELECTOR);
    let ro;
    let roChat;
    let roHandle;
    if (el && typeof window.ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measureAppSidebarWidth());
      ro.observe(el);
    }
    if (chatEl && typeof window.ResizeObserver !== 'undefined') {
      roChat = new ResizeObserver(() => measureChatWidths());
      roChat.observe(chatEl);
    }
    if (handleEl && typeof window.ResizeObserver !== 'undefined') {
      roHandle = new ResizeObserver(() => measureChatWidths());
      roHandle.observe(handleEl);
    }

    return () => {
      window.removeEventListener('resize', measureAppSidebarWidth);
      window.removeEventListener('resize', measureChatWidths);
      if (ro) ro.disconnect();
      if (roChat) roChat.disconnect();
      if (roHandle) roHandle.disconnect();
    };
  }, [measureAppSidebarWidth, measureChatWidths]);

    // Broadcast "waiting for response" status for header UI (WorkflowHeader)
    useEffect(() => {
      try {
        window.dispatchEvent(
          new CustomEvent('workflow-waiting-status', {
            detail: { message: waitingForResponse || null }
          })
        );
      } catch (e) {
        // ignore
      }
    }, [waitingForResponse]);
  

  const ensureWebSocketConnection = useCallback(async () => {
    if (checkConnection()) {
      return true;
    }
    const connected = await reconnectWithRetry(5, 800);
    
    return connected;
  }, [checkConnection, reconnectWithRetry]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const checkAndHandleConnection = async () => {
      const connected = checkConnection();
      
      if (connected) {
        if (!connectionCheckedRef.current) {
          connectionCheckedRef.current = true;
        }
      } else {
        connectionCheckedRef.current = false;
        if (!reconnectAttemptRef.current) {
          reconnectAttemptRef.current = true;
          const reconnected = await reconnectWithRetry(5, 800);    
          if (reconnected) {
            connectionCheckedRef.current = true;
          }
           reconnectAttemptRef.current = false;
        }
      }
    };

    // Small delay to ensure component is fully mounted
    const timeoutId = setTimeout(() => {
      checkAndHandleConnection();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen, checkConnection, reconnectWithRetry]);

  // Monitor connection status and automatically reconnect when disconnected
  useEffect(() => {
    if (!isConnected && !reconnectAttemptRef.current) {
      reconnectAttemptRef.current = true;
      // Small delay to avoid immediate reconnection attempts
      const timeoutId = setTimeout(() => {
        forceReconnect();
        reconnectAttemptRef.current = false;
      }, 1000);
      
      return () => {
        clearTimeout(timeoutId);
        reconnectAttemptRef.current = false;
      };
    } else if (isConnected) {
      reconnectAttemptRef.current = false;
      if (!connectionCheckedRef.current) {
        connectionCheckedRef.current = true;
      }
    }
  }, [isConnected, forceReconnect]);
  
  // Refs for cleanup
  const timeoutRefs = useRef(new Set());
  const abortControllerRef = useRef(null);
  const autoClearTimeoutRef = useRef(null); // Ref to track auto-clear timeout

  // Helper to add timeout with cleanup tracking
  const addTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      timeoutRefs.current.delete(timeoutId);
      callback();
    }, delay);
    timeoutRefs.current.add(timeoutId);
    return timeoutId;
  }, []);

  // Cleanup all timeouts
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current.clear();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear auto-clear timeout on unmount
      if (autoClearTimeoutRef.current) {
        clearTimeout(autoClearTimeoutRef.current);
        autoClearTimeoutRef.current = null;
      }
    };
  }, []);

  // Limit array sizes to prevent memory issues
  const limitArraySize = useCallback((array, maxSize) => {
    if (array.length > maxSize) {
      return array.slice(-maxSize); // Keep most recent items
    }
    return array;
  }, []);

  const agentLabelStyle = useCallback((name) => {
    const s = String(name || '').trim();
    if (!s) return { color: '#6c757d', backgroundColor: '#f3f4f6', borderColor: '#e5e7eb' };
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    const palette = [
      { color: '#1d4ed8', bg: '#eaf2ff', border: '#bfdbfe' },
      { color: '#047857', bg: '#eafff5', border: '#a7f3d0' },
      { color: '#b45309', bg: '#fff4d6', border: '#fde68a' },
      { color: '#7c3aed', bg: '#f3e8ff', border: '#ddd6fe' },
      { color: '#be123c', bg: '#ffecef', border: '#fecdd3' }
    ];
    const p = palette[hash % palette.length];
    return { color: p.color, backgroundColor: p.bg, borderColor: p.border };
  }, []);

  const addConsoleMessage = useCallback((type, message) => {
    setConsoleOutput(prev => {
      const newOutput = [...prev, {
      type,
        message: sanitizeText(message),
      timestamp: new Date().toLocaleTimeString()
      }];
      return limitArraySize(newOutput, MAX_CONSOLE_OUTPUT);
    });
  }, [limitArraySize]);

  // Memoized recursive check function
  const checkGoalSatisfied = useCallback((msg, depth = 0) => {
    if (msg === null || msg === undefined || depth > MAX_RECURSION_DEPTH) return false;
    
    // Handle arrays
    if (Array.isArray(msg)) {
      for (const item of msg) {
        if (checkGoalSatisfied(item, depth + 1)) return true;
      }
      return false;
    }
    
    // Handle objects - check for prototype pollution
    if (typeof msg === 'object' && msg.constructor === Object) {
      // Check direct property
      if (msg.is_goal_satisfied === true) {
        return true;
      }
      
      // Recursively check all object properties
      for (const key in msg) {
        if (Object.prototype.hasOwnProperty.call(msg, key)) {
          // Prevent prototype pollution
          if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            continue;
          }
          const value = msg[key];
          if (value !== null && value !== undefined && typeof value === 'object') {
            if (checkGoalSatisfied(value, depth + 1)) return true;
          }
        }
      }
    }
    
    return false;
  }, []);

  // Helper function to safely get nested value from object using path array
  const getNestedValue = useCallback((obj, paths) => {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const path of paths) {
      const keys = Array.isArray(path) ? path : [path];
      let value = obj;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          value = undefined;
          break;
        }
      }
      if (value !== undefined) return value;
    }
    return undefined;
  }, []);

  // Helper function to extract msg from nested object
  const extractMsg = useCallback((obj) => {
    if (!obj || typeof obj !== 'object') return undefined;
    // Priority: msg.ninfo.msg > msg > ninfo.msg > ninfo.message
    if (obj.msg?.ninfo?.msg !== undefined) return obj.msg.ninfo.msg;
    if (obj.msg !== undefined) return obj.msg;
    if (obj.ninfo?.msg !== undefined) return obj.ninfo.msg;
    if (obj.ninfo?.message !== undefined) return obj.ninfo.message;
    return undefined;
  }, []);

  // Helper function to extract result from server message
  const extractResult = useCallback((message) => {
    if (!message || typeof message !== 'object') {
      return message;
    }

    // Extract agentId using optimized path checking
    const agentIdPaths = [
      'agentId', 'nodeId', 'agent_id', 'uid', 'agentUid',
      ['agent', 'agentId'], ['agent', 'uid'], ['agent', 'agent_id'], ['agent', 'nodeId'],
      ['data', 'agentId'], ['data', 'agent_id'], ['data', 'uid'], ['data', 'agentUid'], ['data', 'nodeId'],
      ['result', 'agentId'], ['result', 'agent_id'], ['result', 'uid'], ['result', 'agentUid'], ['result', 'nodeId'],
      ['agent', 'data', 'agentId'], ['agent', 'data', 'uid'], ['agent', 'data', 'agent_id'], ['agent', 'data', 'nodeId']
    ];
    const agentId = getNestedValue(message, agentIdPaths);
    
    // Extract status
    const status = message?.status || message?.state || 
                   message?.result?.status || message?.agent?.status || message?.data?.status;
    
    // Check execution and completion status
    const executingStatuses = ['running', 'executing', 'processing', 'active'];
    const completedStatuses = ['completed', 'success', 'finished', 'done'];
    const isExecuting = executingStatuses.includes(status) || 
                        message?.executing || message?.running || message?.isRunning === true || 
                        agentId !== undefined;
    const isCompleted = message?.finished || message?.isCompleted === true || message?.completed || 
                        completedStatuses.includes(status);
    
    // Handle agent highlighting
    if (agentId != null && isExecuting) {
      const normalizedAgentId = String(agentId);
      const normalizedCurrentId = currentAgentIdRef.current ? String(currentAgentIdRef.current) : null;

      let nodeId = normalizedAgentId;
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalizedAgentId);
      
      if (isUuid && window.agentUidToNodeIdMap instanceof Map) {
        const mappedNodeId = window.agentUidToNodeIdMap.get(normalizedAgentId);
        if (mappedNodeId) nodeId = mappedNodeId;
      }
      
      if (normalizedCurrentId !== normalizedAgentId) {
        const previousNodeId = currentNodeIdRef.current;
        window.dispatchEvent(new CustomEvent('agent-stop-all'));
        currentAgentIdRef.current = normalizedAgentId;
        currentNodeIdRef.current = nodeId;
        
        addTimeout(() => {
          window.dispatchEvent(new CustomEvent('agent-run', { detail: { nodeId } }));
        }, previousNodeId !== null ? 150 : 0);
      }
    }
    
    // Stop highlighting if completed
    if (isCompleted && currentNodeIdRef.current !== null) {
      window.dispatchEvent(new CustomEvent('agent-stop', {
        detail: { nodeId: currentNodeIdRef.current }
      }));
      currentAgentIdRef.current = null;
      currentNodeIdRef.current = null;
    }
    
    // Check for common result fields in priority order
    const resultFields = ['result', 'response', 'data', 'output', 'content', 'answer', 'message'];
    for (const field of resultFields) {
      const value = message[field];
      if (value !== undefined) {
        // If it's an object, try to extract msg from it
        if (typeof value === 'object' && value !== null) {
          const msgValue = extractMsg(value);
          if (msgValue !== undefined) return msgValue;
        }
        return value;
      }
    }
    
    // Check for msg fields in nested locations
    const msgValue = extractMsg(message);
    if (msgValue !== undefined) return msgValue;
    
    // Check nested locations for msg
    if (message.data && typeof message.data === 'object') {
      const msgValue = extractMsg(message.data);
      if (msgValue !== undefined) return msgValue;
    }
    if (message.result && typeof message.result === 'object') {
      const msgValue = extractMsg(message.result);
      if (msgValue !== undefined) return msgValue;
    }
    
    // Exclude request-like messages
    if (message.action === 'INIT' || message.query || message.app_config) {
      return null;
    }
    
    // Return message itself as fallback
    return message;
  }, [addTimeout, getNestedValue, extractMsg]);

  // Listen for WebSocket messages
  // IMPORTANT: This useEffect must run regardless of isOpen state to capture messages
  useEffect(() => {
    function handleMessageReceived(event) {
      try {
        let message = event.detail;
        if (!message) {
          return;
        }

        if (message?.ninfo?.projectId !== projectId) {
          return;
        }
        setIsWorkflowRunning(true);

        // Check if goal is satisfied in the original message
        const goalSatisfied = checkGoalSatisfied(message);
        
        const result = extractResult(message);
        const goalSatisfiedInResult = result ? checkGoalSatisfied(result) : false;
        
        if (goalSatisfied || goalSatisfiedInResult) {
          // Stop highlighting all agents
          window.dispatchEvent(new CustomEvent('agent-stop-all'));
          currentAgentIdRef.current = null;
          currentNodeIdRef.current = null;
          
          // Dispatch workflow-stop event to update button state 
          if (!workflowStopDispatchedRef.current) {
            window.dispatchEvent(new CustomEvent('workflow-stop'));
            workflowStopDispatchedRef.current = true;
          }
        }
        let displayResult = message?.ninfo?.msg;
        
        // Check for agent_goal_satisfied event at both top level and in ninfo.msg
        const eventType = displayResult?.event || message?.event;
        const isAgentGoalSatisfied = eventType === 'agent_goal_satisfied';
        
        // Extract status from message
       let status = displayResult?.status || message?.status;
        
        // Format the result for display
        const resultString = typeof displayResult === 'object' 
          ? JSON.stringify(displayResult, null, 2) 
          : String(displayResult);
        
        // Sanitize result string
        const sanitizedResult = sanitizeText(resultString);
        
        // Context Data removed from UI; don't duplicate payload into jsonData.

        switch (displayResult?.event || message?.event) {
          case workflowEvents.EVENT_REQUEST_EXECUTE:
            setWaitingForResponse("Preparing...");
            break;
        
          case workflowEvents.EVENT_PIPE_FLOW_START:
          case workflowEvents.EVENT_AGENT_EXECUTE_START:
            setWaitingForResponse("Running...");
            break;
        
          default:
            break;
        }
        
        // Add to structured log list (with size limit)
        setLogItems(prev => {
          const agentName =
            message?.agentName ||
            displayResult?.agentName ||
            message?.agent ||
            displayResult?.agent ||
            message?.data?.agentName ||
            displayResult?.data?.agentName ||
            message?.ninfo?.agentName ||
            displayResult?.ninfo?.agentName ||
            null;
          const newItems = [...prev, {
            id: `${Date.now()}-${Math.random()}`, // More unique ID
            direction: 'in',
            text: sanitizedResult,
            agentName,
            raw: displayResult || message, // Store the msg field or full message
            timestamp: new Date().toLocaleTimeString(),
            status: status ? String(status).toUpperCase() : null // Store status in uppercase
          }];
          return limitArraySize(newItems, MAX_LOG_ITEMS);
        });

        // Helper function to extract and display agent result
        const displayAgentResult = (goalResult, agentId, agentName, rawMessage) => {
          if (!goalResult) return false;
          
          // Track agent ID to prevent duplicates
          const agentIdKey = agentId || goalResult.agent_id || goalResult.name || 'unknown';
          if (displayedAgentIdsRef.current.has(agentIdKey)) {
            return false; // Already displayed
          }
          
          let outResult = null;            
          // First try to get result field
          if (goalResult.result !== undefined && goalResult.result !== null) {
            outResult = goalResult.result;
          } else if (goalResult.content !== undefined && goalResult.content !== null) {
            outResult = goalResult.content;
          } else if (goalResult.reason !== undefined && goalResult.reason !== null) {
            outResult = goalResult.reason;
          } else {
            outResult = goalResult;
          }
          
          if (Array.isArray(outResult)) {
            try {
              // Flatten nested arrays completely
              const flattened = outResult.flat(Infinity);
              // Filter out null/undefined and join strings
              const filtered = flattened.filter(item => item != null);
              if (filtered.length > 0) {
                outResult = filtered.map(item => 
                  typeof item === 'string' ? item : JSON.stringify(item)
                ).join('\n');
              } else {
                outResult = JSON.stringify(outResult, null, 2);
              }
            } catch (e) {
              outResult = JSON.stringify(outResult, null, 2);
            }
          } else if (typeof outResult === 'object' && outResult !== null) {
            // If it's still an object, try to extract string value
            outResult =
              outResult.result ??
              outResult.content ??
              outResult.reason ??
              outResult.data ??
              outResult.error ??
              JSON.stringify(outResult, null, 2);
          }

          // Convert to string if not already; if empty, don't add a row.
          if (outResult == null || outResult === '') {
            return false;
          } else if (typeof outResult !== 'string') {
            outResult = JSON.stringify(outResult, null, 2);
          }

          // Determine status - prefer SUCCESS for goal satisfied
          const statusText = goalResult.is_goal_satisfied === true || goalResult.is_goal_satisfied === 'True'
            ? 'SUCCESS'
            : (goalResult.approved_status || goalResult.status || 'SUCCESS');

          // Mark this agent as displayed
          displayedAgentIdsRef.current.add(agentIdKey);

          // Immediately add the result as an "out" direction log item
          setLogItems(prev => {
            const newItems = [
              ...prev,
              {
                id: `${Date.now()}-${Math.random()}`,
                direction: 'out',
                text: outResult,
                agentName: agentName || goalResult?.name || null,
                raw: rawMessage,
                timestamp: new Date().toLocaleTimeString(),
                status: statusText ? String(statusText).toUpperCase() : 'SUCCESS'
              }
            ];
            return limitArraySize(newItems, MAX_LOG_ITEMS);
          });
          
          return true;
        };

        // When an agent's goal is satisfied, immediately show that agent's result in the console
        if (isAgentGoalSatisfied) {
          // Extract goal_result from multiple possible locations
          const goalResult =
            message?.data?.goal_result ||
            displayResult?.data?.goal_result ||
            message?.goal_result ||
            displayResult?.goal_result ||
            message?.result ||
            displayResult?.result ||
            null;

          const agentId = message?.agentId || displayResult?.agentId || message?.agent_id || displayResult?.agent_id;
          const agentName = message?.agentName || displayResult?.agentName;
          
          displayAgentResult(goalResult, agentId, agentName, displayResult || message);
        }

        // When agent_execute_complete arrives, show the agent's result if it has goal_result
        const isAgentExecuteComplete = eventType === workflowEvents.EVENT_AGENT_EXECUTE_COMPLETE;
        if (isAgentExecuteComplete) {
          // Extract goal_result from multiple possible locations
          const goalResult =
            message?.data?.goal_result ||
            displayResult?.data?.goal_result ||
            message?.goal_result ||
            displayResult?.goal_result ||
            null;

          if (goalResult) {
            const agentId = message?.agentId || displayResult?.agentId || message?.agent_id || displayResult?.agent_id;
            const agentName = message?.agentName || displayResult?.agentName;
            
            displayAgentResult(goalResult, agentId, agentName, displayResult || message);
          }
        }

        if (typeof displayResult === 'object') {

          // Check if this is a workflow-level completion (entire workflow done) vs agent-level completion
          const isWorkflowCompletion =
            displayResult.event === workflowEvents.EVENT_REQUEST_COMPLETED ||
            displayResult.event === workflowEvents.EVENT_REQUEST_FAILED;

          // Treat explicit completion events and terminal statuses as "final"
          const isFinalStatus =
            isWorkflowCompletion ||
            displayResult.event === workflowEvents.EVENT_AGENT_EXECUTE_COMPLETE ||
            displayResult.event === workflowEvents.EVENT_AGENT_EXECUTE_ERROR ||
            displayResult.status === 'SUCCESS' ||
            displayResult.status === messageStatus.FAILED ||
            displayResult.status === 'ERROR';
        
          if (isFinalStatus) {
            // Only set waiting response to null and workflow running to false for workflow-level completions
            // For agent-level completions, keep the workflow running state so subsequent agents can still execute
            if (isWorkflowCompletion) {
              setWaitingForResponse(null);
              setIsWorkflowRunning(false);
            }
            
            // Normalize status
            let derivedStatus = displayResult.status;
            
            // Force status = 'FAILED' when event is error
            if (displayResult.event === workflowEvents.EVENT_AGENT_EXECUTE_ERROR) {
              derivedStatus = 'FAILED';
            }

            // When a final message contains multiple results (e.g., from multiple agents),
            // add an entry for each result so they all show up in the console.
            if (Array.isArray(displayResult.results) && displayResult.results.length > 0) {
              setLogItems(prev => {
                const newItems = [...prev];

                displayResult.results.forEach((agentResult, index) => {
                  // Check if this agent's result has already been displayed
                  const agentIdKey = agentResult?.agent_id || agentResult?.name || agentResult?.agentId || `agent-${index}-${localStorage.getItem('Uid')}`;
                  
                  // Only add if not already displayed
                  if (!displayedAgentIdsRef.current.has(agentIdKey)) {
                    const outResult =
                      agentResult?.result ??
                      agentResult?.error ??
                      agentResult?.data?.error ??
                      agentResult;

                    // Mark as displayed
                    displayedAgentIdsRef.current.add(agentIdKey);

                    newItems.push({
                      id: `${Date.now()}-${Math.random()}`,
                      direction: 'out',
                      text: typeof outResult === 'string' ? outResult : JSON.stringify(outResult, null, 2),
                      agentName: agentResult?.agentName || agentResult?.agent_name || agentResult?.name || agentResult?.agentId || null,
                    // Preserve the original payload but annotate which result index this entry came from
                    raw: { ...displayResult, agentResultIndex: index, agentResult },
                    timestamp: new Date().toLocaleTimeString(),
                    status: derivedStatus ? derivedStatus.toUpperCase() : null
                    });
                  }
                });

                return limitArraySize(newItems, MAX_LOG_ITEMS);
              });
            } else {
              const outResult =
                displayResult?.results?.[0]?.result ??
                displayResult?.error ??
                displayResult?.data?.error ??
                null;
              if (outResult == null || outResult === '') return;
      
              setLogItems(prev => {
                const newItems = [
                  ...prev,
                  {
                    id: `${Date.now()}-${Math.random()}`,
                    direction: 'out',
                    text: JSON.stringify(outResult, null, 2),
                    agentName: displayResult?.agentName || displayResult?.agent || displayResult?.data?.agentName || null,
                    raw: displayResult,
                    timestamp: new Date().toLocaleTimeString(),
                    status: derivedStatus ? derivedStatus.toUpperCase() : null
                  }
                ];
                return limitArraySize(newItems, MAX_LOG_ITEMS);
              });
            }
          }
        }
        
        

      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        addConsoleMessage('error', 'Failed to parse message: ' + error.message);
      }
    }

    // Add event listener
    window.addEventListener('agentMessageReceived', handleMessageReceived);
    
    return () => {
      window.removeEventListener('agentMessageReceived', handleMessageReceived);
    };
  }, [checkGoalSatisfied, extractResult, addConsoleMessage, limitArraySize]);

  // Listen for outgoing workflow messages (sent via DrawWorkFlow)
  useEffect(() => {
    function handleWorkflowSent(event) {
      try {
        const payload = event.detail;
        
        // Extract text content from payload
        let textContent = '';
        if (typeof payload === 'string') {
          // If payload is already a string, use it directly
          textContent = payload;
        } else if (typeof payload === 'object' && payload !== null) {
          // Try to extract text from common fields
          textContent = payload.query || payload.message || payload.text || payload.data || payload.content || '';
          // If still no text found, check if it's a simple object with a single string value
          if (!textContent && Object.keys(payload).length === 1) {
            const firstValue = Object.values(payload)[0];
            if (typeof firstValue === 'string') {
              textContent = firstValue;
            }
          }
          // If we still don't have text, use empty string (don't show JSON)
          if (!textContent) {
            textContent = '';
          }
        } else {
          // For other types, convert to string
          textContent = String(payload || '');
        }
        
        // Sanitize the text content
        const sanitizedText = sanitizeText(textContent);
        const sanitizedPayload = typeof payload === 'object' ? payload : { data: payload };
        
        setLogItems(prev => {
          const newItems = [...prev, {
            id: `${Date.now()}-${Math.random()}`,
            direction: 'out',
            text: sanitizedText,
            raw: sanitizedPayload,
            timestamp: new Date().toLocaleTimeString()
          }];
          return limitArraySize(newItems, MAX_LOG_ITEMS);
        });
        
        // Show typing animation in left side view
        setWaitingForResponse("Pending...");
      } catch (e) {
      }
    }
    window.addEventListener('workflow-message-sent', handleWorkflowSent);
    return () => window.removeEventListener('workflow-message-sent', handleWorkflowSent);
  }, [limitArraySize]);

  // Debounced check for existing logItems (only runs when logItems changes significantly)
  useEffect(() => {
    if (logItems.length === 0) return;
    
    const timeoutId = addTimeout(() => {
      let goalSatisfiedFound = false;
      for (const item of logItems) {
        if (item.raw && checkGoalSatisfied(item.raw)) {
          goalSatisfiedFound = true;
          break;
        }
        // Also check the text if it's a JSON string
        if (item.text && typeof item.text === 'string') {
          const parsed = safeJsonParse(item.text);
          if (parsed && checkGoalSatisfied(parsed)) {
            goalSatisfiedFound = true;
            break;
          }
        }
      }
      
      // If goal is satisfied in existing logs, stop the workflow
      if (goalSatisfiedFound && !workflowStopDispatchedRef.current) {
        window.dispatchEvent(new CustomEvent('agent-stop-all'));
        currentAgentIdRef.current = null;
        currentNodeIdRef.current = null;
        window.dispatchEvent(new CustomEvent('workflow-stop'));
        workflowStopDispatchedRef.current = true;
      }
    }, DEBOUNCE_DELAY);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutRefs.current.delete(timeoutId);
      }
    };
  }, [logItems.length, checkGoalSatisfied, addTimeout]); // Only depend on length to avoid excessive checks

  // Listen for workflow run events to reset the flag and track workflow state
  useEffect(() => {
    function handleWorkflowRun(event) {
      // Reset the flag when a new workflow starts
      workflowStopDispatchedRef.current = false;
      setIsWorkflowRunning(true);
      setIsWorkflowExecuting(false); // Reset executing state when new workflow starts
      
      // Clear any existing auto-clear timeout when a new workflow starts
      if (autoClearTimeoutRef.current) {
        clearTimeout(autoClearTimeoutRef.current);
        autoClearTimeoutRef.current = null;
      }
      
      // Reset displayed agent IDs tracking for new workflow
      displayedAgentIdsRef.current.clear();
    }
    
    function handleWorkflowStop(event) {
      setIsWorkflowRunning(false);
      setIsWorkflowExecuting(false); // Reset executing state when workflow stops
      setWaitingForResponse(null); // Hide typing animation when workflow stops
      // Clear the message input when workflow stops
      draftQueryRef.current = '';
      setMessageInput('');
    }
    
    // Listen for workflow events (request_execute, request_completed, errors)
    function handleWorkflowEvent(event) {
      const eventType = event.detail?.eventType;
      if (eventType === workflowEvents.EVENT_REQUEST_EXECUTE) {
        setIsWorkflowExecuting(true);
        // Clear any existing auto-clear timeout when a new execution starts
        if (autoClearTimeoutRef.current) {
          clearTimeout(autoClearTimeoutRef.current);
          autoClearTimeoutRef.current = null;
        }
      } else if (eventType === 'request_completed' || 
                 eventType === 'request_failed' ||
                 eventType === 'agent_execute_error' ||
                 eventType === 'agent_execute_timeout' ||
                 eventType === 'agent_execute_cancelled' ||
                 eventType === 'agent_execute_failed') {
        setIsWorkflowExecuting(false);
        
        if (autoClearTimeoutRef.current) {
          clearTimeout(autoClearTimeoutRef.current);
        }
      }
    }
    
    // Listen for errors in WebSocket messages
    function handleErrorMessage(event) {
      // Check if the message contains error information
      const message = event.detail;
      if (message && (message.error || message.status === 'error' || (typeof message === 'string' && message.toLowerCase().includes('error')))) {
        setIsWorkflowExecuting(false);
        setWaitingForResponse(null); // Hide typing animation on error
      }
    }

    function handleAgentsChanged(event) {
      const { hasAgents: agentsExist, hasTasks: tasksExist } = event.detail || {};
      setHasAgents(agentsExist || false);
      setHasTasks(tasksExist || false);
    }
    
    window.addEventListener('workflow-run', handleWorkflowRun);
    window.addEventListener('workflow-stop', handleWorkflowStop);
    window.addEventListener('workflow-event', handleWorkflowEvent);
    window.addEventListener('agentMessageReceived', handleErrorMessage);
    window.addEventListener('workflow-agents-changed', handleAgentsChanged);

    return () => {
      window.removeEventListener('workflow-run', handleWorkflowRun);
      window.removeEventListener('workflow-stop', handleWorkflowStop);
      window.removeEventListener('workflow-event', handleWorkflowEvent);
      window.removeEventListener('agentMessageReceived', handleErrorMessage);
      window.removeEventListener('workflow-agents-changed', handleAgentsChanged);
    };
  }, []);

  // Listen for workflow stop events to clear highlighting
  useEffect(() => {
    function handleWorkflowStop(event) {
      // Clear highlighting state
      if (currentNodeIdRef.current !== null) {
        window.dispatchEvent(new CustomEvent('agent-stop-all'));
        currentAgentIdRef.current = null;
        currentNodeIdRef.current = null;
      }
    }
    window.addEventListener('workflow-stop', handleWorkflowStop);
    return () => window.removeEventListener('workflow-stop', handleWorkflowStop);
  }, []);

  // Cleanup: stop highlighting when component unmounts
  useEffect(() => {
    return () => {
      if (currentNodeIdRef.current !== null) {
        window.dispatchEvent(new CustomEvent('agent-stop', {
          detail: { nodeId: currentNodeIdRef.current }
        }));
        currentAgentIdRef.current = null;
        currentNodeIdRef.current = null;
      }
    };
  }, []);

  // Auto-scroll to top when new output is added (latest message shown first)
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = 0;
    }
  }, [consoleOutput.length, jsonData.length, logItems.length]); // Only depend on lengths

  // Focus input field when drawer opens and focusInput is true
  useEffect(() => {
    if (isOpen && focusInput && messageInputRef.current && !isMinimized) {
      // Use setTimeout to ensure the drawer animation completes before focusing
      const timeoutId = setTimeout(() => {
        messageInputRef.current?.focus();
      }, 350); // Slightly longer than the drawer transition (0.3s)
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, focusInput, isMinimized]);

  // Memoized filtered and reversed log items - separate for 'out' and 'in'
  const filteredOutLogItems = useMemo(() => {
    const filtered = logItems.filter(item => {
      if (item.direction !== 'out') return false;
      if (filter !== 'all' && item.direction !== filter) return false;
      if (!search) return true;
      return item.text?.toLowerCase().includes(search.toLowerCase());
    });
    return filtered;
  }, [logItems, filter, search]);


    // Auto-scroll left chat to bottom when new outgoing messages are received
    useEffect(() => {
      if (chatMessagesScrollRef.current) {
        // Scroll to bottom to show newest message (messages are in chronological order)
        chatMessagesScrollRef.current.scrollTop = chatMessagesScrollRef.current.scrollHeight;
      }
    }, [filteredOutLogItems.length]); // Scroll when new messages are added

    
  const filteredInLogItems = useMemo(() => {
    const filtered = logItems.filter(item => {
      if (item.direction !== 'in') return false;
      if (filter !== 'all' && item.direction !== filter) return false;
      if (!search) return true;
      return item.text?.toLowerCase().includes(search.toLowerCase());
    });
    return filtered.reverse();
  }, [logItems, filter, search]);

  // Memoized reversed arrays
  const reversedConsoleOutput = useMemo(() => [...consoleOutput].reverse(), [consoleOutput]);

  const handleClear = useCallback(() => {
    setConsoleOutput([]);
    setJsonData([]);
    setLogItems([]);
    // Clear auto-clear timeout when manually clearing messages
    if (autoClearTimeoutRef.current) {
      clearTimeout(autoClearTimeoutRef.current);
      autoClearTimeoutRef.current = null;
    }
    // Stop highlighting when clearing messages
    if (currentNodeIdRef.current !== null) {
      window.dispatchEvent(new CustomEvent('agent-stop', {
        detail: { nodeId: currentNodeIdRef.current }
      }));
      currentAgentIdRef.current = null;
      currentNodeIdRef.current = null;
    }
  }, []);

  const handleOpenConsoleClearAndShowChat = useCallback(() => {
    // Open/expand console if minimized
    if (isMinimized) {
      setIsMinimized(false);
    }
    // Toggle between chat only (100%) and split view (50%)
    if (leftColumnWidth === 100) {
      setLeftColumnWidth(50); // Show both chat and console
    } else {
      setLeftColumnWidth(100); // Show only chat
    }
  }, [isMinimized, leftColumnWidth]);

  const handlePlayMessage = useCallback(() => {
    // Don't allow sending if workflow is running or a send is already in flight
    if (isWorkflowRunning || sendingQueryRef.current) {
      return;
    }

    const query = draftQueryRef.current.trim();
    if (!query) return;

    sendingQueryRef.current = true;
    draftQueryRef.current = '';
    setMessageInput('');

    // Show the query in the chat, then run the existing execute-workflow path.
    window.dispatchEvent(new CustomEvent('workflow-message-sent', {
      detail: query
    }));

    // Keep the socket up so the response can stream back, but do not
    // block or cancel the workflow execute call on that connection.
    ensureWebSocketConnection().catch(() => {});

    window.dispatchEvent(new CustomEvent('execute-workflow', {
      detail: { query }
    }));
    sendingQueryRef.current = false;
  }, [isWorkflowRunning, ensureWebSocketConnection]);


  const getMessageStyle = useCallback((type) => {
    switch (type) {
      case 'error':
        return { color: '#dc3545', backgroundColor: '#f8d7da' };
      case 'success':
        return { color: '#155724', backgroundColor: '#d4edda' };
      case 'warning':
        return { color: '#856404', backgroundColor: '#fff3cd' };
      case 'info':
        return { color: '#0c5460', backgroundColor: '#d1ecf1' };
      case 'input':
        return { color: '#495057', backgroundColor: '#e9ecef' };
      case 'output':
        return { color: '#495057', backgroundColor: '#f8f9fa' };
      default:
        return { color: '#495057', backgroundColor: '#f8f9fa' };
    }
  }, []);

  // Safe clipboard write with error handling
  const handleCopy = useCallback(async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showNotificationGlobal('Copied to clipboard', 'success', 'bottomRight');

      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showNotificationGlobal('Copied to clipboard', 'success', 'bottomRight');
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
      addConsoleMessage('error', 'Failed to copy to clipboard');
    }
  }, [addConsoleMessage]);

  // Safe file download with cleanup
  const handleSave = useCallback((text, itemId) => {
    try {
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `message-${itemId || Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Cleanup URL after a delay
      addTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error('Failed to save file:', err);
      addConsoleMessage('error', 'Failed to save file');
    }
  }, [addConsoleMessage, addTimeout]);

  // Resize handlers
  const handleResize = useCallback((e) => {
    if (!isResizingRef.current) return;
    
    // Calculate maximum height to prevent drawer from going above header
    const headerHeight = 60; // Approximate header height
    const maxDrawerHeight = window.innerHeight - headerHeight;
    
    const deltaY = resizeStartYRef.current - e.clientY; 
    const newHeight = Math.max(100, Math.min(maxDrawerHeight, resizeStartHeightRef.current + deltaY)); // Min 100px, Max based on viewport
    setDrawerHeight(newHeight);
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    if (handleResizeRef.current) {
      document.removeEventListener('mousemove', handleResizeRef.current);
    }
    if (handleResizeEndRef.current) {
      document.removeEventListener('mouseup', handleResizeEndRef.current);
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Update refs when handlers change
  useEffect(() => {
    handleResizeRef.current = handleResize;
    handleResizeEndRef.current = handleResizeEnd;
  }, [handleResize, handleResizeEnd]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizingRef.current = true;
    setIsResizing(true);
    resizeStartYRef.current = e.clientY;
    resizeStartHeightRef.current = drawerHeight;
    if (handleResizeRef.current) {
      document.addEventListener('mousemove', handleResizeRef.current);
    }
    if (handleResizeEndRef.current) {
      document.addEventListener('mouseup', handleResizeEndRef.current);
    }
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [drawerHeight]);

  // Column resize handlers
  const handleColumnResize = useCallback((e) => {
    if (!isResizingColumnRef.current) return;
    
    const deltaX = e.clientX - resizeColumnStartXRef.current;
    const containerWidth = window.innerWidth - (chatVisible ? chatWidth : 0);
    const deltaPercent = (deltaX / containerWidth) * 100;
    const newWidth = Math.max(20, Math.min(80, resizeColumnStartWidthRef.current + deltaPercent));
    setLeftColumnWidth(newWidth);
  }, [chatVisible, chatWidth]);

  const handleColumnResizeEnd = useCallback(() => {
    isResizingColumnRef.current = false;
    setIsResizingColumn(false);
    if (handleColumnResizeRef.current) {
      document.removeEventListener('mousemove', handleColumnResizeRef.current);
    }
    if (handleColumnResizeEndRef.current) {
      document.removeEventListener('mouseup', handleColumnResizeEndRef.current);
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Update column resize refs when handlers change
  useEffect(() => {
    handleColumnResizeRef.current = handleColumnResize;
    handleColumnResizeEndRef.current = handleColumnResizeEnd;
  }, [handleColumnResize, handleColumnResizeEnd]);

  const handleColumnResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizingColumnRef.current = true;
    setIsResizingColumn(true);
    resizeColumnStartXRef.current = e.clientX;
    resizeColumnStartWidthRef.current = leftColumnWidth;
    if (handleColumnResizeRef.current) {
      document.addEventListener('mousemove', handleColumnResizeRef.current);
    }
    if (handleColumnResizeEndRef.current) {
      document.addEventListener('mouseup', handleColumnResizeEndRef.current);
    }
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftColumnWidth]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      if (isResizingRef.current) {
        if (handleResizeRef.current) {
          document.removeEventListener('mousemove', handleResizeRef.current);
        }
        if (handleResizeEndRef.current) {
          document.removeEventListener('mouseup', handleResizeEndRef.current);
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setIsResizing(false);
        isResizingRef.current = false;
      }
      if (isResizingColumnRef.current) {
        if (handleColumnResizeRef.current) {
          document.removeEventListener('mousemove', handleColumnResizeRef.current);
        }
        if (handleColumnResizeEndRef.current) {
          document.removeEventListener('mouseup', handleColumnResizeEndRef.current);
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setIsResizingColumn(false);
        isResizingColumnRef.current = false;
      }
    };
  }, []);

  // Update height when minimized state changes
  useEffect(() => {
    if (isMinimized) {
      // Store current height before minimizing
      if (drawerHeight > 35) {
        previousHeightRef.current = drawerHeight;
      }
      setDrawerHeight(35);
    } else if (drawerHeight === 35) {
      // If expanding from minimized state, restore to previous height or default
      setDrawerHeight(previousHeightRef.current);
    }
  }, [isMinimized, drawerHeight]);

  // Expand drawer when opening if expandOnOpen is true
  useEffect(() => {
    if (isOpen && expandOnOpen && isMinimized) {
      setIsMinimized(false);
      if (onExpandComplete) {
        onExpandComplete();
      }
    }
  }, [isOpen, expandOnOpen, isMinimized, onExpandComplete]);

  // Calculate responsive positioning.
  // Requirement:
  // - Drawer can overlap the Category panel
  // - Drawer must NOT overlap the left app chat panel
  // Must be defined before early return to comply with React Hooks rules
  const getConsoleDrawerStyles = useCallback(() => {
    // Calculate maximum height to prevent drawer from going above header
    // Header is typically around 50-60px, leaving some margin for safety
    const headerHeight = 60; // Approximate header height
    const maxDrawerHeight = window.innerHeight - headerHeight;
    
    // Ensure drawerHeight doesn't exceed maximum
    const constrainedHeight = Math.min(drawerHeight, maxDrawerHeight);
    
    // Calculate height based on open and minimized state
    // When closed or minimized, show 35px height (header only)
    const calculatedHeight = (!isOpen || isMinimized) ? 35 : constrainedHeight;
    
    // When closed, show 35px height (header only)
    // Calculate how much to translate up - when closed, show only 35px at bottom
    const translateY = isOpen ? 0 : constrainedHeight - 35;
    
    const baseStyles = {
      position: 'fixed',
      bottom: 0,
      // Align with the Category panel start (never overlap global app sidebar nor the project chat panel).
      left: `${appSidebarWidth + chatPanelWidth + chatHandleWidth}px`,
      right: 0,
      height: `${calculatedHeight}px`,
      maxHeight: `${maxDrawerHeight}px`, // Ensure it never exceeds max height
      backgroundColor: '#fff',
      borderTop: '1px solid #e1e5e9',
      boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      transform: `translateY(${translateY}px)`,
      transition: isResizing ? 'none' : 'all 0.3s ease-in-out' // Disable transition while resizing
    };

    return baseStyles;
  }, [isOpen, isMinimized, drawerHeight, isResizing, appSidebarWidth, chatPanelWidth, chatHandleWidth]);

  const consoleDrawerStyles = getConsoleDrawerStyles();

  return (
    <>
      {/* Add bounce animation for typing indicator */}
      <style>{`
        @keyframes typing-bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .typing-dot-animation {
          animation: typing-bounce 1.4s ease-in-out infinite;
        }
        .typing-dot-animation-delay-1 {
          animation: typing-bounce 1.4s ease-in-out infinite;
          animation-delay: 0.2s;
        }
        .typing-dot-animation-delay-2 {
          animation: typing-bounce 1.4s ease-in-out infinite;
          animation-delay: 0.4s;
        }
      `}</style>
    <div style={consoleDrawerStyles}>
      {/* Resize Handle */}
      {!isMinimized && (
        <div
          onMouseDown={handleResizeStart}
          style={{
            height: '4px',
            width: '100%',
            cursor: 'row-resize',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Drag to resize"
        >
          <div
            style={{
              width: '60px',
              height: '3px',
              backgroundColor: '#ccc',
              borderRadius: '2px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#999';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ccc';
            }}
          />
        </div>
      )}
      
      {/* Header */}
      <div
        style={{
          padding: '5px 10px',
          borderBottom: '1px solid #e1e5e9',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaTerminal style={{ color: '#6c757d', fontSize: 13 }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: '#495057' }}>
            Logs
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Minimize/Expand button */}
          <button
            onClick={() => {
              if (isMinimized) {
                setIsMinimized(false);
              } else {
                setIsMinimized(true);
              }
              if (onExpandComplete) {
                onExpandComplete();
              }
            }}
            style={{
              padding: '4px 6px',
              border: '1px solid #ddd',
              borderRadius: 4,
              backgroundColor: '#f8f9fa',
              color: '#495057',
              fontSize: 12,
              cursor: isWorkflowRunning ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              opacity: isWorkflowRunning ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isWorkflowRunning) {
                e.currentTarget.style.backgroundColor = '#e9ecef';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            title={isWorkflowRunning ? 'Workflow is running' : (isMinimized ? 'Expand' : 'Minimize')}
            aria-label={isWorkflowRunning ? 'Workflow is running' : (isMinimized ? 'Expand' : 'Minimize')}
          >
            {isMinimized ? <i className="fa fa-external-link-square"></i> : <FaMinus size={10} />}
          </button>
          
          {/* Close button */}
          <button
            onClick={() => {
              // If workflow is running, minimize instead of closing
              if (isWorkflowRunning) {
                setIsMinimized(true);
              } else {
                onClose();
              }
            }}
            title={isWorkflowRunning ? "Minimize (workflow is running)" : "Close"}
            aria-label={isWorkflowRunning ? "Minimize (workflow is running)" : "Close"}
            style={{
              padding: '4px 6px',
              border: '1px solid #eee',
              borderRadius: 4,
              backgroundColor: '#eee',
              color: '#000',
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <FaTimes size={10} />
          </button>
        </div>
      </div>

      {/* Two-Column Layout - Only show when not minimized and is open */}
      {!isMinimized && isOpen && (
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#fff'
        }}>
          {/* Left Column - Chat View */}
          <div 
            ref={chatViewRef}
            style={{
              width: `${leftColumnWidth}%`,
              display: 'flex',
              flexDirection: 'column',
              borderRight: '1px solid #e1e5e9',
              backgroundColor: '#fff',
              overflow: 'hidden'
            }}
          >
            {/* Chat View Header */}
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid #e1e5e9',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              zIndex: 5
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  boxShadow: '0 0 8px #1976d2'
                }} />
                <span style={{ 
                  fontWeight: 600, 
                  fontSize: 13, 
                  color: '#495057' 
                }}>
                  Chat Messages
                </span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontSize: 11,
                  fontWeight: 600
                }}>
                  {filteredOutLogItems.length}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={handleClear}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    background: '#fff',
                    color: '#6c757d',
                    fontSize: 11,
                    cursor: isWorkflowRunning ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: isWorkflowRunning ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isWorkflowRunning) {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                  title={isWorkflowRunning ? 'Workflow is running' : 'Clear Messages'}
                  aria-label={isWorkflowRunning ? 'Workflow is running' : 'Clear Messages'}
                >
                  Clear
                </button>

                <button
                  onClick={handleOpenConsoleClearAndShowChat}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    background: '#fff',
                    color: '#6c757d',
                    fontSize: 12,
                    cursor: isWorkflowRunning ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s ease',
                    opacity: isWorkflowRunning ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isWorkflowRunning) {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                  }}
                  title={isWorkflowRunning ? 'Workflow is running' : 'Toggle Console View and Clear Messages'}
                  aria-label={isWorkflowRunning ? 'Workflow is running' : 'Toggle Console View and Clear Messages'}
                >
                  <FaTerminal size={12} />
                </button>
              </div>
            </div>

            {/* Chat Messages List */}
            <div 
              ref={chatMessagesScrollRef}
              style={{ 
                flex: 1, 
                overflowY: 'auto',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12
              }}>
                {filteredOutLogItems.length === 0 ? (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#868e96',
                    fontSize: 13,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '40px 20px'
                  }}>
                    No messages yet. Start a conversation to see messages here.
                  </div>
                ) : (
                  <>
                  {filteredOutLogItems.map((item, idx) => {
                    const itemId = item.id || idx;
                    const text = item.text || '';
                    const agentLabel =
                      item?.agentName ||
                      item?.raw?.agentName ||
                      item?.raw?.agent ||
                      item?.raw?.data?.agentName ||
                      item?.raw?.ninfo?.agentName ||
                      item?.raw?.results?.[0]?.agentName ||
                      item?.raw?.results?.[0]?.agent_name ||
                      item?.raw?.results?.[0]?.name ||
                      null;
                    const agentColors = agentLabelStyle(agentLabel);
                                        
                    // Determine message type and styling
                    const getMessageType = () => {
                      if (item.direction === 'in') return { 
                        icon: FaArrowDown, 
                        color: '#1976d2', 
                        bgColor: '#e3f2fd',
                        borderColor: '#1976d2'
                      };
                      if (item.direction === 'out') return { 
                        icon: FaArrowUp, 
                        color: '#f59e0b',
                        bgColor: '#fff8e1',
                        borderColor: '#f59e0b'
                      };
                      const lowerText = text.toLowerCase();
                      if (lowerText.includes('error') || lowerText.includes('failed') || lowerText.includes('could not')) {
                        return { 
                          icon: FaExclamationCircle, 
                          color: '#dc3545', 
                          bgColor: '#fee',
                          borderColor: '#dc3545'
                        };
                      }
                      return { 
                        icon: FaInfoCircle, 
                        color: '#6c757d', 
                        bgColor: '#f8f9fa',
                        borderColor: '#ddd'
                      };
                    };
                    
                    const messageType = getMessageType();
                    const IconComponent = messageType.icon;
                    
                    // All messages in Chat Messages section are 'out' (left-aligned)
                    return (
                      <div 
                        key={itemId} 
                      >
                        <div
                        className={`console-message-container ${item?.status === messageStatus.SUCCESS || item?.status === messageStatus.FAILED ? 'message-row' : ''}`}
                          style={{
                            borderRadius: 8,
                            overflow: 'hidden',
                            // border: `1px solid ${messageType.borderColor}20`,
                            backgroundColor:
                              item?.status === messageStatus.SUCCESS
                                ? "#673ab729"
                                : item?.status === messageStatus.FAILED
                                  ? "#f8d7da"
                                  : "#f7f7f7",
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            width: leftColumnWidth === 100 ? 
                            (item?.status === messageStatus.SUCCESS || item?.status === messageStatus.FAILED ? '50%' : '50%')
                            : '100%',
                            float: leftColumnWidth === 100 
                              ? (item?.status === messageStatus.SUCCESS || item?.status === messageStatus.FAILED ? 'left' : 'right')
                              : 'none',
                            alignSelf: leftColumnWidth === 100 
                              ? (item?.status === messageStatus.SUCCESS || item?.status === messageStatus.FAILED ? 'flex-start' : 'flex-end')
                              : 'stretch',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div 
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 18,
                              padding: '12px 16px'
                            }}
                          >
                            {item?.status !== messageStatus.SUCCESS && item?.status !== messageStatus.FAILED && <IconComponent 
                              style={{ 
                                color: messageType.color, 
                                fontSize: 12,
                                flexShrink: 0,
                                marginTop: 2
                              }} 
                            />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontSize: 12, 
                                color: '#6c757d',
                                marginBottom: 4,
                                display: 'flex',
                                position: 'relative',
                                alignItems: 'center',
                                gap: 8
                              }}>
                                <FaClock size={10} />
                                <span>{item.timestamp}</span>
                                {agentLabel ? (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      padding: '4px 10px',
                                      borderRadius: 999,
                                      fontWeight: 800,
                                      fontSize: 12,
                                      border: `1px solid ${agentColors.borderColor}`,
                                      color: '#2196F3',
                                      backgroundColor: '#ffffff',
                                      maxWidth: 240,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={agentLabel}
                                  >
                                    {agentLabel}
                                  </span>
                                ) : null}
                                {(item?.status === messageStatus.SUCCESS || item?.status === messageStatus.FAILED) && (
                                  <div className="copy-icon-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'absolute', right: 0 }}>
                                    <i
                                      className="fa-solid fa-copy"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(text);
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#3b82f6';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#6c757d';
                                      }}
                                    ></i>
                                  </div>
                                )}

                              </div>

                              <div
                                style={{
                                  fontSize: 13,
                                  color: '#111827',
                                  whiteSpace: 'pre-wrap',
                                  overflow: 'visible',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.6,
                                  textAlign: 'left',
                                  padding: '10px 12px',
                                  borderRadius: 10,
                                  backgroundColor: 'rgba(255,255,255,0.6)',
                                  border: '1px solid rgba(15, 23, 42, 0.06)',
                                  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
                                }}
                              >
                                <ReactMarkdown
                                  skipHtml
                                  components={{
                                    p: ({ children }) => <p style={{ margin: 0, lineHeight: 1.6 }}>{children}</p>,
                                    ol: ({ children }) => <ol style={{ margin: '8px 0 0', paddingLeft: 18 }}>{children}</ol>,
                                    ul: ({ children }) => <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>{children}</ul>,
                                    li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
                                    strong: ({ children }) => <strong style={{ fontWeight: 800 }}>{children}</strong>,
                                    code: ({ children }) => (
                                      <code
                                        style={{
                                          fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                          fontSize: 12,
                                          background: 'rgba(15, 23, 42, 0.06)',
                                          padding: '1px 6px',
                                          borderRadius: 6
                                        }}
                                      >
                                        {children}
                                      </code>
                                    )
                                  }}
                                >
                                  {String(text || '')}
                                </ReactMarkdown>
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Waiting Response Indicator with Spinner and Animation */}
                  {(() => {
                    // Show waiting indicator only if waiting for response
                    if (!waitingForResponse) return null;
                    
                    return (
                      <div
                        style={{
                          borderRadius: 12,
                          padding: '16px 20px',
                          minHeight: '60px',
                          marginTop: 'auto',
                          marginBottom: 8,
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)',
                          border: '1px solid rgba(59, 130, 246, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          animation: 'fadeInUp 0.3s ease-out',
                          position: 'relative',
                          width: leftColumnWidth === 100 ? "55%" : "100%",
                          overflow: 'hidden'
                        }}
                      >
                        {/* Animated Background Gradient */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.15), transparent)',
                          animation: 'shimmer 2s ease-in-out infinite'
                        }} />
                        
                        {/* Text and Typing Dots */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#1e293b',
                            marginBottom: 6,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <span>{waitingForResponse}</span>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                animation: 'typingDot 1.4s ease-in-out infinite'
                              }}
                            />
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                animation: 'typingDot 1.4s ease-in-out infinite 0.2s'
                              }}
                            />
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                animation: 'typingDot 1.4s ease-in-out infinite 0.4s'
                              }}
                            />
                          </div>
                        </div>
                        
                        {/* CSS Animations */}
                        <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                          @keyframes typingDot {
                            0%, 60%, 100% {
                              opacity: 0.3;
                              transform: translateY(0) scale(0.8);
                            }
                            30% {
                              opacity: 1;
                              transform: translateY(-4px) scale(1.2);
                            }
                          }
                          @keyframes fadeInUp {
                            from {
                              opacity: 0;
                              transform: translateY(10px);
                            }
                            to {
                              opacity: 1;
                              transform: translateY(0);
                            }
                          }
                          @keyframes shimmer {
                            0% {
                              left: -100%;
                            }
                            100% {
                              left: 100%;
                            }
                          }
                        `}</style>
                      </div>
                    );
                  })()}
                  </>
                )}
              </div>

              {/* Input Box with Play Button - At bottom of chat view */}
              <div style={{
                padding: '8px 10px',
                borderTop: '1px solid #e1e5e9',
                background: '#fff',
                zIndex: 10,
                boxShadow: '0 -2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={messageInput}
                    onChange={(e) => {
                      draftQueryRef.current = e.target.value;
                      setMessageInput(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || isWorkflowRunning) {
                        return;
                      }
                      e.preventDefault();
                      handlePlayMessage();
                    }}
                    disabled={isWorkflowRunning}
                    placeholder={isWorkflowRunning ? "Workflow is running..." : "Enter your query here..."}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      background: '#fff',
                      color: '#333',
                      fontSize: 14,
                      outline: 'none',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      transition: 'all 0.2s ease',
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                      opacity: isWorkflowRunning ? 0.6 : 1,
                      cursor: isWorkflowRunning ? 'not-allowed' : 'text'
                    }}
                    onFocus={(e) => {
                      if (!isWorkflowRunning) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.05), 0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ddd';
                      e.target.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.05)';
                    }}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handlePlayMessage}
                    disabled={!messageInput.trim() || isWorkflowRunning}
                    style={{
                      padding: '13px',
                      border: 'none',
                      borderRadius: 8,
                      background: (messageInput.trim() && !isWorkflowRunning)
                        ? '#3b82f6'
                        : '#e5e7eb',
                      color: (messageInput.trim() && !isWorkflowRunning)
                        ? '#fff'
                        : '#999',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: (messageInput.trim() && !isWorkflowRunning) ? 'pointer' : 'not-allowed',
                      opacity: (!messageInput.trim() || isWorkflowRunning) ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'all 0.2s ease',
                      boxShadow: (messageInput.trim() && !isWorkflowRunning)
                        ? '0 2px 8px rgba(59, 130, 246, 0.3)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (messageInput.trim() && !isWorkflowRunning) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (messageInput.trim()) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                      }
                    }}
                    title={isWorkflowRunning ? "Workflow is running..." : "Send Query"}
                    aria-label={!isWorkflowRunning ? "Workflow is running..." : "Send Query"}
                  >
                    <FaPlay size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Resizable Divider */}
            <div
              onMouseDown={handleColumnResizeStart}
              style={{
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: '#e1e5e9',
                position: 'relative',
                zIndex: 6,
                transition: isResizingColumn ? 'none' : 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                if (!isResizingColumn) {
                  e.currentTarget.style.backgroundColor = '#e1e5e9';
                }
              }}
            >
              <div style={{
                width: '2px',
                height: '40px',
                backgroundColor: '#999',
                borderRadius: 2
              }} />
            </div>

            {/* Right Column - Console View */}
            <div 
              ref={consoleViewRef}
              style={{
                width: `${100 - leftColumnWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fafafa',
                overflow: 'hidden'
              }}
            >
              {/* Console View Header */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e1e5e9',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 5
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaTerminal style={{ 
                    color: '#22c55e',
                    fontSize: 14
                  }} />
                  <span style={{ 
                    fontWeight: 600, 
                    fontSize: 13, 
                    color: '#495057' 
                  }}>
                    Console Output
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: 12,
                    backgroundColor: '#d1fae5',
                    color: '#22c55e',
                    fontSize: 11,
                    fontWeight: 600
                  }}>
                    {filteredInLogItems.length + reversedConsoleOutput.length}
                  </span>
                </div>
              </div>

              {/* Console Output Content */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto',
                padding: '12px',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: 12,
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}>
                {/* Incoming Messages Section */}
                {filteredInLogItems.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    {filteredInLogItems.map((item, idx) => {
                      const itemId = item.id || `in-${idx}`;
                      const isExpanded = expandedItems.has(itemId);
                      const text = item.text || '';
                      const agentLabel =
                        item?.agentName ||
                        item?.raw?.agentName ||
                        item?.raw?.agent ||
                        item?.raw?.data?.agentName ||
                        item?.raw?.ninfo?.agentName ||
                        null;
                      
                      const parsedJson = safeJsonParse(text);
                      const formattedText =
                        parsedJson ? JSON.stringify(parsedJson, null, 2) : String(text || '');
                      
                      // Determine message type and styling
                      const getMessageType = () => {
                        if (item.direction === 'in') return { 
                          icon: FaArrowDown, 
                          color: '#1976d2', 
                          bgColor: '#e3f2fd',
                          borderColor: '#1976d2'
                        };
                        const lowerText = text.toLowerCase();
                        if (lowerText.includes('error') || lowerText.includes('failed') || lowerText.includes('could not')) {
                          return { 
                            icon: FaExclamationCircle, 
                            color: '#dc3545', 
                            bgColor: '#fee',
                            borderColor: '#dc3545'
                          };
                        }
                        return { 
                          icon: FaInfoCircle, 
                          color: '#6c757d', 
                          bgColor: '#f8f9fa',
                          borderColor: '#ddd'
                        };
                      };
                      
                      const messageType = getMessageType();
                      const IconComponent = messageType.icon;
                      return (
                        <div 
                          key={itemId} 
                          id={`console-item-${itemId}`}
                          style={{
                            display: 'flex',
                            justifyContent: 'stretch',
                            width: '100%',
                            marginBottom: 8
                          }}
                        >
                          <div
                            style={{
                              borderRadius: 8,
                              overflow: 'hidden',
                              border: `1px solid ${messageType.borderColor}20`,
                              backgroundColor: messageType.bgColor,
                              transition: 'all 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              width: '100%',
                              maxWidth: '100%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div
                                onClick={() => {
                                  const next = new Set(expandedItems);
                                  if (next.has(itemId)) next.delete(itemId);
                                  else next.add(itemId);
                                  setExpandedItems(next);
                                  setTimeout(() => {
                                    const el = document.getElementById(`console-item-${itemId}`);
                                    if (el && typeof el.scrollIntoView === 'function') {
                                      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                    }
                                  }, 0);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 12,
                                  padding: '8px 10px 6px',
                                  cursor: 'pointer',
                                  flexDirection: 'row-reverse'
                                }}
                              >
                                <IconComponent
                                  style={{
                                    color: messageType.color,
                                    fontSize: 12,
                                    flexShrink: 0,
                                    marginTop: 2
                                  }}
                                />

                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: '#6c757d',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      justifyContent: 'flex-end',
                                      flexDirection: 'row-reverse'
                                    }}
                                  >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <FaClock size={10} />
                                      <span>{item.timestamp}</span>
                                    </span>
                                    {item.direction ? (
                                      <span
                                        style={{
                                          padding: '2px 6px',
                                          borderRadius: 4,
                                          backgroundColor: messageType.color + '20',
                                          color: messageType.color,
                                          fontSize: 10,
                                          fontWeight: 600,
                                          textTransform: 'uppercase'
                                        }}
                                      >
                                        {item.direction}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>

                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexShrink: 0
                                  }}
                                >
                                  {isExpanded ? (
                                    <FaChevronUp size={12} style={{ color: '#6c757d' }} />
                                  ) : (
                                    <FaChevronDown size={12} style={{ color: '#6c757d' }} />
                                  )}
                                  {isExpanded ? (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <FaCopy
                                        style={{ cursor: 'pointer', color: '#6c757d', fontSize: 12 }}
                                        title="Copy"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopy(text);
                                        }}
                                      />
                                      <FaRegSave
                                        style={{ cursor: 'pointer', color: '#6c757d', fontSize: 12 }}
                                        title="Save"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSave(text, itemId);
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  fontSize: 13,
                                  color: '#212529',
                                  whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                                  overflow: isExpanded ? 'visible' : 'hidden',
                                  textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                  wordBreak: isExpanded ? 'break-word' : 'normal',
                                  lineHeight: 1.5,
                                  fontFamily: isExpanded ? 'Monaco, Menlo, \"Ubuntu Mono\", monospace' : 'inherit',
                                  padding: '0 10px 10px'
                                }}
                              >
                                {isExpanded && parsedJson ? (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ padding: '6px 6px 2px', background: 'transparent' }}
                                  >
                                    <ReactJson
                                      src={parsedJson}
                                      name={false}
                                      collapsed={2}
                                      enableClipboard={false}
                                      displayDataTypes
                                      displayObjectSize
                                      quotesOnKeys={false}
                                      sortKeys={false}
                                      theme={JSON_VIEW_THEME}
                                      style={{ background: 'transparent' }}
                                    />
                                  </div>
                                ) : (
                                  (isExpanded ? formattedText : String(text || ''))
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Context Data removed */}

                {/* Console Messages Section */}
                {reversedConsoleOutput.length > 0 && (
                  <div>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#6c757d',
                      marginBottom: 8,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Console Messages
                    </div>
                    {reversedConsoleOutput.map((item, index) => {
                      const itemId = `console-${index}`;
                      const isExpanded = expandedItems.has(itemId);
                      const message = item.message || '';
                      const messageStyle = getMessageStyle(item.type);
                      const isError = item.type === 'error';
                      
                      return (
                        <div
                          key={itemId}
                          style={{
                            marginBottom: 8,
                            borderRadius: 6,
                            overflow: 'hidden',
                            border: `1px solid ${messageStyle.color}30`,
                            backgroundColor: messageStyle.backgroundColor,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}
                        >
                          <div
                            onClick={() => {
                              const newExpanded = new Set(expandedItems);
                              if (isExpanded) {
                                newExpanded.delete(itemId);
                              } else {
                                newExpanded.add(itemId);
                              }
                              setExpandedItems(newExpanded);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 12,
                              padding: '12px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            {isError && (
                              <FaExclamationCircle 
                                style={{ 
                                  color: messageStyle.color, 
                                  fontSize: 14,
                                  flexShrink: 0,
                                  marginTop: 2
                                }} 
                              />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontSize: 10, 
                                color: '#6c757d',
                                marginBottom: 4,
                                fontFamily: 'system-ui, sans-serif'
                              }}>
                                [{item.timestamp}]
                                {item.type ? ` • ${item.type.toUpperCase()}` : null}
                              </div>
                              <div style={{ 
                                fontSize: 12,
                                color: messageStyle.color,
                                whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                                overflow: isExpanded ? 'visible' : 'hidden',
                                textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                wordBreak: isExpanded ? 'break-word' : 'normal',
                                lineHeight: 1.5
                              }}>
                                {message}
                              </div>
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              {isExpanded ? (
                                <FaChevronUp size={12} style={{ color: messageStyle.color }} />
                              ) : (
                                <FaChevronDown size={12} style={{ color: messageStyle.color }} />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty State */}
                {filteredInLogItems.length === 0 && reversedConsoleOutput.length === 0 && (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#868e96',
                    fontSize: 13,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '60px 20px',
                    minHeight: '200px'
                  }}>
                    <FaTerminal size={32} style={{ 
                      marginBottom: 16,
                      opacity: 0.3
                    }} />
                    <div>Waiting for console output...</div>
                    <div style={{ fontSize: 11, marginTop: 8, opacity: 0.7 }}>
                      Console messages will appear here
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      )}
    </div>
    </>
  );
};

export default ConsoleDrawer;
